/**
 * Schemas dos formulários do painel.
 *
 * Validar aqui é cortesia com o usuário (resposta imediata, sem viagem de
 * rede). Quem AUTORIZA e valida de verdade é o servidor — estes schemas não
 * são a defesa de nada.
 */
import { z } from 'zod';
import { WORKSPACE_ROLES } from '@mgdlead/shared';

const email = z
  .string()
  .trim()
  .min(1, 'Informe seu e-mail.')
  .email('Esse e-mail não parece válido.');

/** No login não damos dica sobre a força da senha: só se foi digitada. */
const currentPassword = z.string().min(1, 'Informe sua senha.');

const newPassword = z
  .string()
  .min(8, 'A senha precisa de pelo menos 8 caracteres.')
  .max(128, 'Senha longa demais (máximo de 128 caracteres).');

const personName = z
  .string()
  .trim()
  .min(2, 'Informe seu nome.')
  .max(80, 'Nome longo demais (máximo de 80 caracteres).');

export const loginSchema = z.object({
  email,
  password: currentPassword,
  rememberMe: z.boolean(),
});

export const signupSchema = z
  .object({
    name: personName,
    email,
    password: newPassword,
    passwordConfirmation: z.string(),
  })
  .refine((values) => values.password === values.passwordConfirmation, {
    path: ['passwordConfirmation'],
    message: 'As senhas não conferem.',
  });

export const forgotPasswordSchema = z.object({ email });

export const resetPasswordSchema = z
  .object({
    password: newPassword,
    passwordConfirmation: z.string(),
  })
  .refine((values) => values.password === values.passwordConfirmation, {
    path: ['passwordConfirmation'],
    message: 'As senhas não conferem.',
  });

export const resendVerificationSchema = z.object({ email });

export const workspaceSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Informe o nome do workspace.')
    .max(60, 'Nome longo demais (máximo de 60 caracteres).'),
  timezone: z.string().min(1, 'Escolha o fuso horário.'),
  locale: z.string().min(1, 'Escolha o idioma.'),
});

export const profileSchema = z.object({ name: personName });

export const inviteMemberSchema = z.object({
  email,
  // OWNER não se convida: ele só existe por transferência de propriedade
  role: z.enum(['ADMIN', 'ANALYST', 'MEMBER']),
});

/**
 * Nome do monitorador. Os limites e as frases são os MESMOS do `nameSchema` da
 * api (`monitor.router.ts`): divergir faria o campo aceitar aqui e o servidor
 * recusar depois, que é a pior forma de validação.
 */
export const monitorNameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'O nome precisa de pelo menos 2 caracteres.')
    .max(80, 'O nome vai até 80 caracteres.'),
});

/**
 * Campanha. Os limites e as frases são os MESMOS do `campaign.router.ts` da api —
 * divergir faria o campo aceitar aqui e o servidor recusar depois, que é a pior forma de
 * validação. `code` não está aqui porque não é editável em lugar nenhum: quem o gera é o
 * servidor, e ele é imutável.
 */
export const campaignSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Dê um nome com pelo menos 2 caracteres.')
    .max(80, 'O nome da campanha tem no máximo 80 caracteres.'),
  description: z.string().trim().max(500, 'A descrição tem no máximo 500 caracteres.'),
});

/**
 * Redirecionador. Os limites e as frases são os MESMOS do `redirector.router.ts` e do
 * `slug.ts` da api — divergir faria o campo aceitar aqui e o servidor recusar depois.
 *
 * Duas ausências deliberadas, ambas espelhando o contrato:
 *
 *  - **não há edição de `slug`** (só criação): o endereço já saiu do produto no instante em
 *    que alguém o colou num anúncio, e trocá-lo faria o anúncio de ontem apontar para o nada;
 *  - **não há campo de URL de destino**: o fallback é escolhido por `id` dentro da allowlist
 *    (invariante 7), e é por isso que `fallbackUrlId` é um id e não um endereço.
 *
 * `strategy` DEIXOU de ser ausência (migration 0013): são três valores, e o `superRefine`
 * abaixo cobra o grupo fixo do mesmo jeito que o `assertStrategyCoherent` do servidor — com a
 * MESMA frase. Divergir faria o campo aceitar aqui e o servidor recusar depois; o que este
 * schema NÃO tenta é conferir se o grupo está vinculado à campanha, porque isso só o servidor
 * sabe (e é ele quem recusa, com o nome do grupo na frase).
 */
export const redirectorSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Dê um nome com pelo menos 2 caracteres.')
      .max(80, 'O nome do link tem no máximo 80 caracteres.'),
    campaignId: z.string().uuid('Escolha a campanha que vai receber estes cliques.'),
    /**
     * Vazio = o servidor sorteia. Com valor, a normalização de verdade (acento, espaço, hífen
     * duplicado) é do servidor; aqui só se barra o que é obviamente curto ou longo demais,
     * para o erro aparecer sem uma viagem de rede.
     */
    slug: z
      .string()
      .trim()
      .max(48, 'O endereço do link tem no máximo 48 caracteres.')
      .refine((value) => value.length === 0 || value.length >= 3, {
        message: 'O endereço precisa de pelo menos 3 caracteres (letras, números e hífen).',
      }),
    /** `""` = sem destino de reserva. */
    fallbackUrlId: z.string(),
    /** Os três valores do enum do banco. O padrão é o mesmo do servidor. */
    strategy: z.enum(['FILL_SEQUENTIAL', 'RANDOM', 'SPECIFIC_GROUP']),
    /** `""` = nenhum grupo fixado. Só tem sentido com `SPECIFIC_GROUP`. */
    pinnedGroupId: z.string(),
    /** B6: o que o link faz quando nenhum grupo pode receber. */
    noGroupAction: z.enum(['FALLBACK_URL', 'CONTINGENCY_GROUP']),
    /** `""` = nenhum grupo de contingência. Só tem sentido com `CONTINGENCY_GROUP`. */
    contingencyGroupId: z.string(),
  })
  .superRefine((values, ctx) => {
    /*
     * A MESMA regra (e a mesma frase) do `assertStrategyCoherent` do servidor: estratégia de
     * grupo fixo sem grupo seria um link que só descobre para onde vai no primeiro clique
     * pago. Aqui ela existe para o erro aparecer no campo, sem viagem de rede.
     */
    if (values.noGroupAction === 'CONTINGENCY_GROUP' && values.contingencyGroupId.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['contingencyGroupId'],
        message:
          'Escolha o grupo de contingência: é para ele que o link manda quem clicar quando nenhum grupo da campanha puder receber.',
      });
    }
    if (values.strategy === 'SPECIFIC_GROUP' && values.pinnedGroupId.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['pinnedGroupId'],
        message:
          'Escolha o grupo fixo: a estratégia “grupo fixo” precisa saber para qual grupo mandar todo mundo.',
      });
    }
  });

/** Destino autorizado da allowlist. `http`/`https` apenas — quem valida de verdade é a api. */
export const allowlistUrlSchema = z.object({
  label: z
    .string()
    .trim()
    .min(2, 'Dê um nome de pelo menos 2 caracteres para reconhecer este destino.')
    .max(80, 'O nome tem no máximo 80 caracteres.'),
  url: z
    .string()
    .trim()
    .min(8, 'Informe uma URL completa, começando com https://')
    .max(2048, 'URL longa demais.')
    .refine((value) => /^https?:\/\/.+/i.test(value), {
      message: 'A URL precisa começar com http:// ou https://',
    }),
});

export const workspaceRoleSchema = z.enum(WORKSPACE_ROLES);

export type LoginValues = z.input<typeof loginSchema>;
export type SignupValues = z.input<typeof signupSchema>;
export type ForgotPasswordValues = z.input<typeof forgotPasswordSchema>;
export type ResetPasswordValues = z.input<typeof resetPasswordSchema>;
export type ResendVerificationValues = z.input<typeof resendVerificationSchema>;
export type WorkspaceValues = z.input<typeof workspaceSchema>;
export type ProfileValues = z.input<typeof profileSchema>;
export type InviteMemberValues = z.input<typeof inviteMemberSchema>;
export type MonitorNameValues = z.input<typeof monitorNameSchema>;
export type CampaignValues = z.input<typeof campaignSchema>;
export type RedirectorValues = z.input<typeof redirectorSchema>;
export type AllowlistUrlValues = z.input<typeof allowlistUrlSchema>;
